import type { ReactNode } from "react"
import { Tabs as TabsPrimitive } from "radix-ui"

import { ABA, CONTAGEM_DA_ABA, CONTAGEM_DA_ABA_ZERO } from "../lib/classes"
import { cn } from "../lib/cn"
import type { Icone } from "./Icones"
import { TabsList, TabsTrigger } from "./Tabs"

const BARRA = "mb-4 flex flex-wrap items-center justify-between gap-2"

export type DescricaoDeAba<C extends string> = {
  chave: C
  rotulo: string
  contagem?: number | null
  dica?: string
  icone?: Icone
}

export function Abas<C extends string>({
  nome,
  rotulo,
  abas,
  atual,
  aoTrocar,
  acao,
  classe,
  classeDaLista,
}: {
  nome: string
  rotulo: string
  abas: DescricaoDeAba<C>[]
  atual: C
  aoTrocar: (chave: C) => void
  acao?: ReactNode
  classe?: string
  classeDaLista?: string
}) {
  return (
    <TabsPrimitive.Root
      value={atual}
      onValueChange={(valor) => aoTrocar(valor as C)}
      asChild
    >
      <div data-slot="abas" className={cn(BARRA, classe)}>
        <TabsList className={cn("max-w-full flex-wrap justify-start", classeDaLista)} aria-label={rotulo}>
          {abas.map(({ icone: IconeDaAba, ...aba }) => (
            <TabsTrigger
              key={aba.chave}
              value={aba.chave}
              id={identificadorDaAba(nome, aba.chave)}
              title={aba.dica}
              className={ABA}
            >
              {IconeDaAba !== undefined && <IconeDaAba tamanho={14} />}
              {aba.rotulo}
              {aba.contagem !== null && aba.contagem !== undefined && (
                <span
                  data-slot="abas-contagem"
                  data-contagem={aba.contagem === 0 ? "zero" : "cheia"}
                  className={cn(CONTAGEM_DA_ABA, aba.contagem === 0 && CONTAGEM_DA_ABA_ZERO)}
                >
                  {aba.contagem.toLocaleString("pt-BR")}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        {acao !== undefined && (
          <span data-slot="abas-acao" className="ml-auto inline-flex items-center">
            {acao}
          </span>
        )}
      </div>
    </TabsPrimitive.Root>
  )
}

export function PainelDaAba({
  nome,
  chave,
  ativa,
  classe,
  children,
}: {
  nome: string
  chave: string
  ativa: boolean
  classe?: string
  children: ReactNode
}) {
  return (
    <div
      data-slot="painel-de-aba"
      className={cn("[&[hidden]]:hidden", classe)}
      role="tabpanel"
      id={identificadorDoPainel(nome, chave)}
      aria-labelledby={identificadorDaAba(nome, chave)}
      hidden={!ativa}
      tabIndex={ativa ? 0 : -1}
    >
      {children}
    </div>
  )
}

function identificadorDaAba(nome: string, chave: string): string {
  return `aba-${nome}-${chave}`
}

function identificadorDoPainel(nome: string, chave: string): string {
  return `painel-${nome}-${chave}`
}
